import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiUserCheck } from 'react-icons/fi';
import UserModal from '../../../components/UserModal';
import ConfirmModal from '../../../components/ConfirmModal';
import Toast from '../../../components/Toast';
import api from '../../../services/api';

const AdminTeamPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (page = 1, search = '', role = '') => {
    try {
      setLoading(true);
      const response = await api.get('/users', {
        params: {
          page,
          limit: itemsPerPage,
          search,
          role
        }
      });
      console.log('API Response:', response.data);
      console.log('Users count:', response.data.users.length);
      console.log('Pagination:', response.data.pagination);
      setUsers((response.data.users || []).slice().sort((a, b) => a.name.localeCompare(b.name)));
      setPagination(response.data.pagination);
      setCurrentPage(page);
      setSearchTerm(search);
      setRoleFilter(role);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load team members. Please try again.');
      setLoading(false);
    }
  };
  
  const handleAddUser = () => {
    setCurrentUser(null);
    setShowUserModal(true);
  };
  
  const handleEditUser = (user) => {
    setCurrentUser(user);
    setShowUserModal(true);
  };
  
  const handleDeleteUser = (user) => {
    setCurrentUser(user);
    setShowDeleteModal(true);
  };
  
  const handleSaveUser = async (userData) => {
    try {
      console.log('Saving user data:', userData);
      if (currentUser) {
        // Update existing user
        console.log('Updating user ID:', currentUser.id);
        const response = await api.put(`/users/${currentUser.id}`, userData);
        console.log('Update response:', response.data);
        setToast({ message: 'Team member updated successfully!', type: 'success' });
        // Refresh current page to show updated data
        fetchUsers(currentPage, searchTerm, roleFilter);
      } else {
        // Create new user
        console.log('Creating new user');
        const response = await api.post('/users', userData);
        console.log('Create response:', response.data);
        setToast({ message: response.data.message || 'Team member created successfully!', type: 'success' });
        // Refresh current page to show new user
        fetchUsers(currentPage, searchTerm, roleFilter);
      }
      setShowUserModal(false);
    } catch (error) {
      console.error('Error saving user:', error);
      console.error('Error details:', error.response?.data);
      setToast({ message: 'Failed to save user: ' + (error.response?.data?.message || error.message), type: 'error' });
    }
  };
  
  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/users/${currentUser.id}`);
      setShowDeleteModal(false);
      // Refresh current page after deletion
      fetchUsers(currentPage, searchTerm, roleFilter);
    } catch (error) {
      console.error('Error deleting user:', error);
      setToast({ message: 'Failed to delete user: ' + (error.response?.data?.message || error.message), type: 'error' });
    }
  };
  
  const handleUpdateRole = async (userId, newRole) => {
    try {
      const response = await api.put(`/users/${userId}/role`, { role: newRole });
      setUsers(users.map(user => user.id === userId ? { ...user, role: newRole } : user));
    } catch (error) {
      console.error('Error updating user role:', error);
      setToast({ message: 'Failed to update user role: ' + (error.response?.data?.message || error.message), type: 'error' });
    }
  };

  const handleActivateUser = async (user) => {
    if (!window.confirm(`Manually activate "${user.name || user.email}"? They will be able to login without email verification.`)) return;
    try {
      await api.put(`/users/${user.id}/activate`);
      setToast({ message: 'User activated successfully!', type: 'success' });
      fetchUsers(currentPage, searchTerm, roleFilter);
    } catch (error) {
      setToast({ message: 'Failed to activate user: ' + (error.response?.data?.message || error.message), type: 'error' });
    }
  };

  const handleSearchChange = (term) => {
    setSearchTerm(term);
    // Debounce search to avoid too many API calls
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      fetchUsers(1, term, roleFilter);
    }, 500);
  };

  const handleRoleFilterChange = (role) => {
    setRoleFilter(role);
    fetchUsers(1, searchTerm, role);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    fetchUsers(1, '', '');
  };

  const goToPage = (page) => {
    fetchUsers(page, searchTerm, roleFilter);
  };

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-800';
      case 'manager': return 'bg-blue-100 text-blue-800';
      case 'employee': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaginationPages = () => {
    const total = pagination.totalPages;
    const current = currentPage;
    const pages = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else {
      pages.push(1);
      if (current > 3) pages.push('...');
      for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i);
      if (current < total - 2) pages.push('...');
      pages.push(total);
    }
    return pages;
  };


  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Team Management</h1>
        <div className="text-center py-8">Loading team members...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Team Management</h1>
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={fetchUsers} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Team Management</h1>
      </div>
      
      {/* Search and Filter */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-64">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search by name or email</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search team members..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select
              value={roleFilter}
              onChange={(e) => handleRoleFilterChange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">All Roles</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="employee">Employee</option>
            </select>
          </div>
          
          <button
            onClick={clearFilters}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Clear
          </button>
          <button
            onClick={handleAddUser}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center text-sm ml-auto"
          >
            <FiPlus className="mr-2" /> Add Team Member
          </button>
        </div>
      </div>

      {users.length === 0 && !loading ? (
        <div className="text-center py-8 bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 mb-4">No team members found</p>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Joining Date
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date of Birth
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mobile
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Task Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Account
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-600 font-medium">
                          {((user.first_name || '')[0] || '') + ((user.last_name || '')[0] || '')}
                        </span>
                      </div>
                      <div className="ml-4">
                        <Link 
                          to={`/admin/team/${user.id}`}
                          className="text-sm font-medium text-blue-600 hover:text-blue-900 hover:underline"
                        >
                          {`${user.first_name || ''} ${user.last_name || ''}`.trim() || 'N/A'}
                        </Link>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={user.role}
                      onChange={(e) => handleUpdateRole(user.id, e.target.value)}
                      disabled={user.id === JSON.parse(localStorage.getItem('user'))?.id}
                      className={`text-sm border-gray-300 rounded-md ${user.id === JSON.parse(localStorage.getItem('user'))?.id ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    >
                      <option value="admin">Admin</option>
                      <option value="manager">Manager</option>
                      <option value="employee">Employee</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {user.joining_date ? new Date(user.joining_date).toLocaleDateString() : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {user.date_of_birth ? new Date(user.date_of_birth).toLocaleDateString() : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {user.mobile_number || '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-wrap gap-1 text-xs">
                      {user.taskCounts && (
                        <>
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded">
                            To Do: {user.taskCounts.todo}
                          </span>
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded">
                            In Progress: {user.taskCounts.in_progress}
                          </span>
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded">
                            Testing: {user.taskCounts.testing}
                          </span>
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded">
                            Review: {user.taskCounts.client_review}
                          </span>
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded">
                            Done: {user.taskCounts.completed}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.is_verified ? (
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-700">Active</span>
                    ) : (
                      <button
                        onClick={() => handleActivateUser(user)}
                        className="flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-700 hover:bg-yellow-200 border border-yellow-300"
                        title="Click to manually activate this user"
                      >
                        <FiUserCheck size={12} /> Activate
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEditUser(user)}
                      className="text-indigo-600 hover:text-indigo-900 mr-3"
                    >
                      <FiEdit2 className="inline" /> Edit
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FiTrash2 className="inline" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {loading && (
        <div className="text-center py-4">
          <div className="text-gray-500">Loading...</div>
        </div>
      )}
      
      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === pagination.totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{((currentPage - 1) * pagination.itemsPerPage) + 1}</span> to{' '}
                <span className="font-medium">{Math.min(currentPage * pagination.itemsPerPage, pagination.totalItems)}</span> of{' '}
                <span className="font-medium">{pagination.totalItems}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                {getPaginationPages().map((page, index) => (
                  page === '...'
                    ? <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm text-gray-500">...</span>
                    : <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          currentPage === page
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                ))}
                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === pagination.totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {showUserModal && (
        <UserModal
          user={currentUser}
          onClose={() => setShowUserModal(false)}
          onSave={handleSaveUser}
        />
      )}

      {showDeleteModal && (
        <ConfirmModal
          title="Delete User"
          message={`Are you sure you want to delete ${currentUser?.name}? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default AdminTeamPage;